import { Injectable } from '@angular/core';

/** Shape of a single cache entry stored in sessionStorage. */
export interface CacheEntry<T> {
  value: T;
  cachedAt: number;
}

@Injectable({ providedIn: 'root' })
export class SessionCacheService {

  /** Read a typed entry from sessionStorage. Returns null on miss or parse error. */
  get<T>(key: string): CacheEntry<T> | null {
    try {
      const serialized = sessionStorage.getItem(key);
      return serialized ? (JSON.parse(serialized) as CacheEntry<T>) : null;
    } catch {
      sessionStorage.removeItem(key);
      return null;
    }
  }

  /** Write a typed entry to sessionStorage, stamping the current timestamp. */
  set<T>(key: string, data: T): void {
    const entry: CacheEntry<T> = { value: data, cachedAt: Date.now() };
    try {
      sessionStorage.setItem(key, JSON.stringify(entry));
    } catch (err) {
      // sessionStorage quota exceeded or disabled — non-fatal, but log for observability
      // eslint-disable-next-line no-console
      console.warn('[SessionCacheService] Failed to write cache entry for key "%s":', key, err);
    }
  }

  /** Remove a single entry by key. */
  remove(key: string): void {
    sessionStorage.removeItem(key);
  }

  /** Remove all entries whose key starts with the given prefix. */
  clearByPrefix(prefix: string): void {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const storageKey = sessionStorage.key(i);
      if (storageKey?.startsWith(prefix)) {
        sessionStorage.removeItem(storageKey);
      }
    }
  }

  /**
   * Returns true when the stored entry is older than `ttlMs` milliseconds,
   * or when no entry exists for `key`.
   */
  isStale(key: string, ttlMs: number): boolean {
    const entry = this.get(key);
    if (!entry) { return true; }
    return Date.now() - entry.cachedAt > ttlMs;
  }
}
